import { useEffect, useRef, useState } from 'react';
import { Dialog } from 'radix-ui';
import { FileTextIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNamespaceCopy, formatCopy } from '@/i18n/ns';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import shareChipCopy from '@/i18n/ns/share-chip';
import { formatCount } from '@/i18n/format';
import { SessionChangedError } from '@/lib/session-guard';
import { CanvasHeaderTools } from '../canvas-footer';
import { useOptionalWorkspace } from '../context';
import { useIntlLocale } from '../intl-locale';
import { emptyTurnContext } from '../types';
import { RunsOn } from '../share-sheet/runs-on';
import {
  AudienceControl,
  PeopleList,
  PersonRow,
  SheetFooter,
  SheetSection,
  ShareSheet,
  ShareTrigger,
  audienceStatus,
  loadColleagues,
  roleOptions,
  sameAudience,
  toSheetAudience,
  toVisibility,
  useColleagues,
  useSheetCopy,
  type SheetAudience,
  type SheetMember,
  type SheetPerson,
} from '../share-sheet/share-sheet';
import type { SavedItem } from './save-to-library';

// The Share button of a saved item. It shows the audience the server actually
// holds and opens the one Share sheet only when this person may change it. It
// imports no audience API: the owner of the audience (SHARE's A14 route)
// supplies the writer for one exact saved tuple. Nothing is claimed before the
// genuine committed tuple and a real read; a change happens only on Save.

export type AudienceItem = {
  visibility: 'private' | 'members' | 'company';
  canShare: boolean;
  sharingRevision?: unknown;
  /** Absent for a person who may not manage sharing: no identities are shown or inferred. */
  audience?: readonly { userId: string; role: string }[];
};
export type AudienceChange = {
  visibility: 'private' | 'members' | 'company';
  audience: readonly { userId: string; role: 'viewer' | 'editor' }[];
};
export type ShareWriter = {
  /** The actual current audience of the saved item. */
  read(): Promise<AudienceItem | { item: AudienceItem }>;
  /** Applies one explicit change; resolves to the ACTUAL applied item metadata. */
  apply(change: AudienceChange): Promise<AudienceItem | { item: AudienceItem }>;
};

const itemOf = (value: AudienceItem | { item: AudienceItem }): AudienceItem =>
  'item' in value ? value.item : value;
const membersOf = (item: AudienceItem): SheetMember[] =>
  (item.audience ?? []).map(({ userId, role }) => ({
    userId,
    role: role === 'editor' ? 'editor' : 'viewer',
  }));

type State =
  | { key: string; phase: 'loading' }
  | { key: string; phase: 'failed' }
  | { key: string; phase: 'ready'; item: AudienceItem; working: boolean };

export function ShareChip({
  itemKey,
  saved,
  writer,
  companyName,
  title,
  resultId,
  colleagues = loadColleagues,
}: {
  /** One value per saved item: a different item starts from its own read. */
  itemKey: string;
  /** The genuine committed tuple. Null or absent: nothing is claimed and nothing is drawn. */
  saved: SavedItem | null | undefined;
  writer: ShareWriter | null | undefined;
  companyName: string;
  /** The item's name, for the sheet's heading. */
  title?: string;
  /** A saved Result's id: the sheet shows, read-only, the connections it runs on. */
  resultId?: string;
  /** The colleagues "Specific people" can add (the share dialog's list). */
  colleagues?: () => Promise<readonly SheetPerson[]>;
}) {
  const copy = useNamespaceCopy(shareChipCopy);
  const panels = useNamespaceCopy(artifactPanelsCopy);
  const locale = useIntlLocale();
  const [state, setState] = useState<State>({ key: itemKey, phase: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [open, setOpen] = useState(false);
  const alive = useRef(true);
  // The item on show NOW and the latest read/apply operation: a completion that belongs to an older item or an older
  // operation must never write into the state of the current one (switching items while an apply is pending).
  const current = useRef({ key: itemKey, operation: 0 });
  current.current.key = itemKey;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  // A different saved item never inherits an open sheet.
  useEffect(() => setOpen(false), [itemKey]);
  const active = Boolean(saved && writer);
  useEffect(() => {
    if (!saved || !writer) return;
    let current = true;
    setState({ key: itemKey, phase: 'loading' });
    writer.read().then(
      (value) =>
        current && setState({ key: itemKey, phase: 'ready', item: itemOf(value), working: false }),
      (error) => {
        if (current && !(error instanceof SessionChangedError))
          setState({ key: itemKey, phase: 'failed' });
      },
    );
    return () => {
      current = false;
    };
    // The tuple is the identity: a new item or a retry reads again.
  }, [itemKey, active, attempt]);
  if (!saved || !writer || state.key !== itemKey) return null;
  const apply = async (change: AudienceChange) => {
    if (state.phase !== 'ready' || state.working) return;
    const key = itemKey;
    const operation = ++current.current.operation;
    const stale = () =>
      !alive.current || current.current.key !== key || current.current.operation !== operation;
    setState({ ...state, working: true });
    try {
      const applied = itemOf(await writer.apply(change));
      if (stale()) return;
      setState({ key, phase: 'ready', item: applied, working: false });
      setOpen(false);
    } catch (error) {
      if (stale() || error instanceof SessionChangedError) return;
      // The audience is whatever the server holds: read it again instead of showing a guess.
      setOpen(false);
      setAttempt((value) => value + 1);
    }
  };
  let body;
  if (state.phase === 'loading') body = null;
  else if (state.phase === 'failed')
    body = (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        data-testid="share-chip-retry"
        onClick={() => setAttempt((v) => v + 1)}
      >
        {panels.retry}
      </Button>
    );
  else {
    const { item } = state;
    const label =
      item.visibility === 'private'
        ? copy.onlyYou
        : item.visibility === 'company'
          ? copy.company
          : item.audience
            ? formatCount(copy.peopleCount, item.audience.length, locale)
            : copy.shared;
    body = item.canShare ? (
      <Dialog.Root
        open={open}
        onOpenChange={(next) => {
          if (state.working) return;
          setOpen(next);
        }}
      >
        <Dialog.Trigger asChild>
          <ShareTrigger
            visibility={item.visibility}
            audienceLabel={label}
            testId="share-chip"
            disabled={state.working}
          />
        </Dialog.Trigger>
        {open && (
          <ChipSheet
            item={item}
            title={title || panels.shareTitle}
            resultId={resultId}
            companyName={companyName}
            working={state.working}
            colleagues={colleagues}
            onApply={(change) => void apply(change)}
            onClose={() => setOpen(false)}
          />
        )}
      </Dialog.Root>
    ) : (
      <span data-testid="share-chip-readonly">{label}</span>
    );
  }
  return (
    <CanvasHeaderTools>
      <span data-testid="share-chip-host" data-phase={state.phase}>
        {body}
      </span>
    </CanvasHeaderTools>
  );
}

function ChipSheet({
  item,
  title,
  resultId,
  companyName,
  working,
  colleagues,
  onApply,
  onClose,
}: {
  item: AudienceItem;
  title: string;
  resultId?: string;
  companyName: string;
  working: boolean;
  colleagues: () => Promise<readonly SheetPerson[]>;
  onApply: (change: AudienceChange) => void;
  onClose: () => void;
}) {
  const sheet = useSheetCopy();
  const locale = useIntlLocale();
  const ws = useOptionalWorkspace();
  const selfId = ws?.user?.id;
  // Editors change what it runs on in a new chat about it, never here.
  const changeInChat =
    ws && resultId
      ? () => {
          onClose();
          ws.send(
            formatCopy(sheet.changesPrompt, { kind: 'result', id: resultId, name: title }),
            emptyTurnContext(),
            true,
            null,
            { newChat: true },
          );
        }
      : undefined;
  const people = useColleagues(colleagues);
  const [audience, setAudience] = useState<SheetAudience>(toSheetAudience(item.visibility));
  const [members, setMembers] = useState<SheetMember[]>(membersOf(item));
  const visibility = toVisibility(audience);
  const dirty =
    visibility !== item.visibility ||
    (visibility === 'members' && !sameAudience(members, membersOf(item)));
  return (
    <ShareSheet
      eyebrow={sheet.eyebrowShare}
      title={title}
      icon={<FileTextIcon />}
      busy={working}
      testId="share-chip-sheet"
      status={
        dirty
          ? sheet.statusPending
          : audienceStatus(sheet, locale, audience, members.length, companyName)
      }
      actions={
        <SheetFooter
          dirty={dirty}
          busy={working}
          disabled={visibility === 'members' && !members.length}
          saveTestId="share-chip-save"
          onSave={() => onApply({ visibility, audience: visibility === 'members' ? members : [] })}
        />
      }
    >
      <SheetSection title={sheet.whoTitle}>
        <AudienceControl
          value={audience}
          companyName={companyName}
          disabled={working}
          onChange={setAudience}
        />
        {audience === 'company' && (
          <PersonRow
            id="company"
            company
            name={formatCopy(sheet.everyone, { company: companyName })}
            detail={sheet.everyoneRowBody}
          />
        )}
        {audience === 'people' && (
          <PeopleList
            members={members}
            colleagues={people.people}
            loading={people.loading}
            failed={people.failed}
            roles={roleOptions(sheet, 'file')}
            exclude={selfId ? [selfId] : []}
            disabled={working}
            onChange={setMembers}
          />
        )}
      </SheetSection>
      {resultId && (
        <RunsOn
          kind="result"
          id={resultId}
          thing="theResult"
          selfId={selfId}
          chatLabel={sheet.editInChat}
          onChangeInChat={changeInChat}
        />
      )}
    </ShareSheet>
  );
}
